import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PageSection } from '../page-section/page-section';
import { ContributorBadge, LeaderboardBoard, MyContributionsResponse, MyContributionsTab, Offer, OfferContributionProposal, OfferContributionsResponse, PlanEntitlements, TopContributor, TopContributorsResponse, UsageStatus, UserContributionSummary } from '../../core/models';
import { I18nService } from '../../core/i18n.service';
import { SessionService } from '../../core/session.service';
import { ApiService, OfferSortKey, SortDirection } from '../../core/api.service';
import { buildCountryCodeByDisplayName, countryDisplayName as isoCountryDisplayName, countryFlagImageUrl, IsoCountryCode, ISO_COUNTRY_CODES, normalizeCountryName } from '../../core/countries';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { safeExternalUrl as sanitizeExternalUrl, safeSameOriginOrHttpImageUrl } from '../../core/url-security';

interface TierLane {
  id: Offer['tier'];
  name: string;
  color: string;
}

interface StatPoint {
  date: string;
  day: string;
  newOffers: number;
  averageFit: number;
}

interface AxisLabel {
  index: number;
  day: string;
  left: number;
  align: 'start' | 'middle' | 'end';
}

type RangePreset = '7d' | '30d' | '365d' | 'custom';
type ChartMetric = 'averageFit' | 'newOffers';
interface OfferFilters {
  status: string;
  title: string;
  university: string;
  country: string;
  fitScore: string;
  deadline: string;
}

interface PaginationItem {
  key: string;
  label: string;
  page?: number;
  disabled?: boolean;
}

interface TierListItem {
  offerId: Offer['id'];
  tier: Offer['tier'];
}

interface CountryFlagItem {
  code: IsoCountryCode;
  name: string;
  flagUrl: string;
}

interface CountrySelectOption extends CountryFlagItem {
  value: string;
}

type ContributionFieldKey = 'deadline' | 'country' | 'university' | 'publishedAt' | 'department' | 'researchAreas' | 'methods' | 'requirements' | 'benefits' | 'description' | 'sourceUrl' | 'applyUrl';

interface ContributionField {
  key: ContributionFieldKey;
  label: string;
  missing: boolean;
  placeholder: string;
}

type ContributionReaction = 'like' | 'dislike';
type DashboardModalKey = 'calendar' | 'offerDetail' | 'contribution' | 'search' | 'addTierOffer' | 'scoreDetails' | 'myContributions';
type DashboardMainTab = 'activity' | 'contributors';

interface LeaderboardCacheEntry {
  loadedAt: number;
  response: TopContributorsResponse;
}

interface ScoreMetricCard {
  key: string;
  labelKey: string;
  value: number;
  icon: string;
}

interface ChartSegment {
  key: string;
  labelKey: string;
  value: number;
  width: number;
  kind?: 'positive' | 'negative' | 'neutral';
}

interface ScoreCompositionSegment extends ChartSegment {
  count: number;
  weight: number;
  points: number;
  color: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [FormsModule, PageSection, LoadingSpinner],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss'
})
export class Dashboard implements OnInit, OnDestroy {
  private static readonly OPEN_DEADLINE_SENTINEL = '9999-12-31';
  readonly statusOrder: Offer['status'][] = ['Interview', 'Applied', 'Liked', 'Seen', 'Archived'];
  readonly statusOptions: Offer['status'][] = ['Seen', 'Liked', 'Applied', 'Interview', 'Archived'];
  readonly pageSizeOptions = [10, 25, 50];
  readonly tierLanes: TierLane[] = [
    { id: 'S', name: 'S', color: '#ff7f7f' },
    { id: 'A', name: 'A', color: '#ffbf7f' },
    { id: 'B', name: 'B', color: '#ffff7f' },
    { id: 'C', name: 'C', color: '#7fff7f' }
  ];
  offers: Offer[] = [];
  selectedOffer: Offer | null = null;
  selectedOfferDetail: Offer | null = null;
  generatedCoverLetter = '';
  coverLetterDraft = '';
  coverLetterRequestMessageKey = '';
  coverLetterUsage: UsageStatus | null = null;
  subscriptionEntitlements: PlanEntitlements | null = null;
  offerContributions: OfferContributionsResponse | null = null;
  topContributors: TopContributor[] = [];
  topContributorsResponse: TopContributorsResponse | null = null;
  myContributions: UserContributionSummary[] = [];
  myContributionsResponse: MyContributionsResponse | null = null;
  selectedLeaderboardBoard: LeaderboardBoard = 'overall';
  activeMyContributionsTab: MyContributionsTab = 'sent';
  selectedScoreContributor: TopContributor | null = null;
  contributionDrafts: Record<ContributionFieldKey, string> = this.emptyContributionDrafts();
  contributionMessage = '';
  isRefreshingOfferDetail = false;
  isLoadingGeneratedCoverLetter = false;
  isLoadingCoverLetterQuota = true;
  isLoadingMatchedOffers = true;
  matchedOffersLoadFailed = false;
  isGeneratingCoverLetter = false;
  isSavingCoverLetter = false;
  isExportingCoverLetterTxt = false;
  isExportingCoverLetterPdf = false;
  isDownloadingIcs = false;
  isLoadingOfferContributions = false;
  isLoadingTopContributors = false;
  isLoadingMyContributions = false;
  topContributorsLoadFailed = false;
  myContributionsLoadFailed = false;
  isSubmittingContribution = false;
  isApplyingOfferFilters = false;
  isClearingOfferFilters = false;
  isAddingTierOffer = false;
  currentOffersPage = 1;
  currentMyContributionsPage = 1;
  customOffersPage = 1;
  offersPageSize = 10;
  readonly myContributionsPageSize = 10;
  offerSortKey: OfferSortKey | null = null;
  offerSortDirection: SortDirection | null = null;
  totalMatchedOffers = 0;
  totalMatchedOfferPages = 1;
  showSearchModal = false;
  showAddTierOfferModal = false;
  showMyContributionsModal = false;
  isOfferDetailScrolled = false;
  selectedContributionField: ContributionField | null = null;
  selectedTierForNewOffer: Offer['tier'] = 'S';
  tierOfferSearch = '';
  offerFilters: OfferFilters = this.emptyOfferFilters();
  draftOfferFilters: OfferFilters = this.emptyOfferFilters();
  tierListItems: TierListItem[] = [];
  tierContextMenu: { offerId: Offer['id']; x: number; y: number } | null = null;
  activeMainTab: DashboardMainTab = 'activity';
  readonly leaderboardBoards: { value: LeaderboardBoard; labelKey: string }[] = [
    { value: 'overall', labelKey: 'dashboard.leaderboards.overall' },
    { value: 'liked', labelKey: 'dashboard.leaderboards.liked' },
    { value: 'sent', labelKey: 'dashboard.leaderboards.sent' },
    { value: 'reviewed', labelKey: 'dashboard.leaderboards.reviewed' }
  ];
  readonly myContributionsTabs: { value: MyContributionsTab; labelKey: string }[] = [
    { value: 'sent', labelKey: 'dashboard.myContributionsModal.sentTab' },
    { value: 'liked', labelKey: 'dashboard.myContributionsModal.likedTab' }
  ];
  readonly defaultBadgeImageUrl = 'assets/badges/complete-profile.svg';

  readonly today = new Date().toISOString().slice(0, 10);
  readonly statPoints: StatPoint[] = this.buildYearlyStatPoints(this.today);
  readonly rangeOptions: { value: RangePreset; labelKey: string }[] = [
    { value: '7d', labelKey: 'dashboard.last7Days' },
    { value: '30d', labelKey: 'dashboard.last30Days' },
    { value: '365d', labelKey: 'dashboard.last365Days' },
    { value: 'custom', labelKey: 'dashboard.customDates' }
  ];
  readonly metricOptions: { value: ChartMetric; labelKey: string }[] = [
    { value: 'averageFit', labelKey: 'dashboard.averageFit' },
    { value: 'newOffers', labelKey: 'dashboard.newOffers' }
  ];
  selectedMetric: ChartMetric = 'averageFit';
  selectedRange: RangePreset = '7d';
  customFrom = '2026-07-08';
  customTo = '2026-07-14';
  chartAnimationKey = 0;
  showChartReveal = true;
  animatedPointCount = 0;
  activeStatPoint: { point: StatPoint; index: number; x: number; tooltipX: number; tooltipY: number } | null = null;
  cursorGuideX: number | null = null;
  private readonly chartPlotDuration = 1400;
  private readonly virtualPlotPointCount = 100;
  private readonly modalAnimationDurationMs = 220;
  private readonly countryCodeByDisplayName = buildCountryCodeByDisplayName(this.i18n.dateLocale());
  readonly countryContributionOptions: CountrySelectOption[] = ISO_COUNTRY_CODES
    .map(code => ({ ...this.countryFlagItem(code, isoCountryDisplayName(code, this.i18n.dateLocale())), value: isoCountryDisplayName(code, this.i18n.dateLocale()) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  private chartAnimationFrame: number | null = null;
  private chartAnimationStartedAt = 0;
  private closingModals = new Set<DashboardModalKey>();
  private modalCloseTimers = new Map<DashboardModalKey, ReturnType<typeof setTimeout>>();
  private readonly leaderboardCacheTtlMs = 5 * 60 * 1000;
  private leaderboardCache: Partial<Record<LeaderboardBoard, LeaderboardCacheEntry>> = {};
  private loadingContributorAvatarKeys = new Set<string>();

  constructor(public i18n: I18nService, public session: SessionService, private api: ApiService) {}

  get tooltipLeftPercent(): number {
    return this.activeStatPoint ? (this.activeStatPoint.x / 500) * 100 : 50;
  }

  get cursorGuideLeftPercent(): number {
    return this.cursorGuideX === null ? 0 : (this.cursorGuideX / 500) * 100;
  }

  get orderedOffers(): Offer[] {
    const filteredOffers = this.filteredOffers;
    return this.hasActiveOfferFilters && this.offerSortKey && this.offerSortDirection
      ? [...filteredOffers].sort((a, b) => this.compareOffers(a, b))
      : filteredOffers;
  }

  get filteredOffers(): Offer[] {
    return this.offers.filter(offer => this.offerMatchesFilters(offer, this.offerFilters));
  }

  get hasActiveOfferFilters(): boolean {
    return Object.values(this.offerFilters).some(value => value.trim().length > 0);
  }

  get offersEmptyStateTitleKey(): string {
    if (this.isLoadingMatchedOffers) return 'dashboard.emptyState.loadingTitle';
    if (this.matchedOffersLoadFailed) return 'dashboard.emptyState.errorTitle';
    if (this.hasActiveOfferFilters) return 'dashboard.emptyState.filteredTitle';
    return 'dashboard.emptyState.noMatchesTitle';
  }

  get offersEmptyStateDescriptionKey(): string {
    if (this.isLoadingMatchedOffers) return 'dashboard.emptyState.loadingDescription';
    if (this.matchedOffersLoadFailed) return 'dashboard.emptyState.errorDescription';
    if (this.hasActiveOfferFilters) return 'dashboard.emptyState.filteredDescription';
    return 'dashboard.emptyState.noMatchesDescription';
  }

  get paginatedOrderedOffers(): Offer[] {
    return this.orderedOffers;
  }

  get totalOffersPages(): number {
    return this.hasActiveOfferFilters ? Math.max(1, Math.ceil(this.filteredOffers.length / this.offersPageSize)) : this.totalMatchedOfferPages;
  }

  get visibleOffersPages(): PaginationItem[] {
    if (this.totalOffersPages <= 7) {
      return this.rangeOfPages(1, this.totalOffersPages).map(page => this.paginationPageItem(page));
    }

    const pages = new Set<number>([
      1,
      this.totalOffersPages,
      ...this.rangeOfPages(this.currentOffersPage - 1, this.currentOffersPage + 1).filter(page => page > 1 && page < this.totalOffersPages)
    ]);
    const sortedPages = [...pages].sort((a, b) => a - b);
    const items: PaginationItem[] = [];

    sortedPages.forEach((page, index) => {
      const previousPage = sortedPages[index - 1];
      if (index > 0 && page - previousPage > 1) {
        items.push({ key: `ellipsis-${previousPage}-${page}`, label: '...', disabled: true });
      }
      items.push(this.paginationPageItem(page));
    });

    return items;
  }

  get visibleMyContributionsPages(): PaginationItem[] {
    const totalPages = this.myContributionsTotalPages;
    if (totalPages <= 7) return this.rangeOfPages(1, totalPages).map(page => this.paginationPageItem(page));

    const pages = new Set<number>([
      1,
      totalPages,
      ...this.rangeOfPages(this.currentMyContributionsPage - 1, this.currentMyContributionsPage + 1).filter(page => page > 1 && page < totalPages)
    ]);
    const sortedPages = [...pages].sort((a, b) => a - b);
    const items: PaginationItem[] = [];
    sortedPages.forEach((page, index) => {
      const previousPage = sortedPages[index - 1];
      if (index > 0 && page - previousPage > 1) items.push({ key: `my-ellipsis-${previousPage}-${page}`, label: '...', disabled: true });
      items.push({ ...this.paginationPageItem(page), key: `my-page-${page}` });
    });
    return items;
  }

  get myContributionsTotalPages(): number {
    return this.myContributionsResponse?.totalPages ?? 1;
  }

  get myContributionsTotalItems(): number {
    return this.myContributionsResponse?.totalItems ?? this.myContributions.length;
  }

  get activeOffers(): number {
    return this.filteredOffers.filter(offer => offer.status !== 'Archived').length;
  }

  get averageFit(): number {
    if (!this.filteredOffers.length) return 0;
    return Math.round(this.filteredOffers.reduce((sum, offer) => sum + offer.fitScore, 0) / this.filteredOffers.length);
  }

  get appliedOrInterviewed(): number {
    return this.filteredOffers.filter(offer => ['Applied', 'Interview'].includes(offer.status)).length;
  }

  get contributionCount(): number {
    return this.topContributorsResponse?.totalActiveContributions ?? 0;
  }

  get visibleStatPoints(): StatPoint[] {
    const { from, to } = this.selectedDateRange;
    const points = this.statPoints.filter(point => point.date >= from && point.date <= to);
    return points.length ? points : this.statPoints.slice(-1);
  }

  get selectedDateRange(): { from: string; to: string } {
    if (this.selectedRange === 'custom') {
      const from = this.normalizeDateInput(this.customFrom, this.statPoints[0].date);
      const to = this.normalizeDateInput(this.customTo, this.today);
      return from <= to ? { from, to } : { from: to, to: from };
    }

    const days = this.selectedRange === '7d' ? 7 : this.selectedRange === '30d' ? 30 : 365;
    return { from: this.shiftDate(this.today, -(days - 1)), to: this.today };
  }

  get xAxisLabels(): AxisLabel[] {
    const points = this.visibleStatPoints;
    const pointCount = points.length;
    if (pointCount === 0) return [];

    const labelCount = pointCount <= 20 ? pointCount : Math.min(20, Math.max(10, Math.ceil(pointCount / 30)));
    const indices = Array.from({ length: labelCount }, (_item, index) => {
      if (labelCount === 1) return 0;
      return Math.round((index / (labelCount - 1)) * (pointCount - 1));
    });

    return [...new Set(indices)].map(index => ({
      index,
      day: points[index].day,
      left: (this.pointX(index) / 500) * 100,
      align: index === 0 ? 'start' : index === pointCount - 1 ? 'end' : 'middle'
    }));
  }

  get maxVisibleOffers(): number {
    return Math.max(0, ...this.visibleStatPoints.map(point => point.newOffers));
  }

  get halfVisibleOffers(): number {
    return Math.round(this.maxVisibleOffers / 2);
  }

  get selectedMetricLabel(): string {
    return this.selectedMetric === 'averageFit' ? this.i18n.t('dashboard.averageFit') : this.i18n.t('dashboard.newOffers');
  }

  offerStatusLabel(status: Offer['status']): string {
    const key = {
      Seen: 'dashboard.statuses.seen',
      Liked: 'dashboard.statuses.liked',
      Applied: 'dashboard.statuses.applied',
      Interview: 'dashboard.statuses.interview',
      Archived: 'dashboard.statuses.archived'
    }[status];
    return this.i18n.t(key);
  }

  countryFlag(country: string): string {
    const code = this.countryCode(country);
    if (!code) return '';

    return countryFlagImageUrl(code);
  }

  countryDisplayName(country: string): string {
    return this.isUnknownCountry(country) ? this.i18n.t('common.unknown') : country;
  }

  offerCountryTitle(offer: Offer): string {
    return this.countryFlagItems(offer).map(item => item.name).join(', ') || this.countryDisplayName(offer.country);
  }

  countryFlagItems(offer: Offer): CountryFlagItem[] {
    if (this.isMultipleCountries(offer.country)) {
      return [];
    }

    const code = this.countryCode(offer.country);
    return code ? [this.countryFlagItem(code as IsoCountryCode, this.countryDisplayName(offer.country))] : [];
  }

  isMultipleCountries(country: string): boolean {
    return normalizeCountryName(country) === 'multiple countries';
  }

  isUnknownCountry(country: string): boolean {
    const normalized = normalizeCountryName(country);
    return !normalized
      || normalized === 'unknown location'
      || normalized === 'unknown'
      || normalized === 'unspecified'
      || normalized === 'n a'
      || normalized === 'not provided'
      || normalized === 'research institution';
  }

  get selectedMetricPath(): string {
    return this.selectedMetric === 'averageFit' ? this.averageFitPath : this.newOffersPath;
  }

  get yAxisMaxLabel(): string {
    return this.selectedMetric === 'averageFit' ? '100%' : `${this.maxVisibleOffers}`;
  }

  get yAxisMidLabel(): string {
    return this.selectedMetric === 'averageFit' ? '50%' : `${this.halfVisibleOffers}`;
  }

  get yAxisMinLabel(): string {
    return this.selectedMetric === 'averageFit' ? '0%' : '0';
  }

  get lineAnimationName(): string {
    return this.chartAnimationKey % 2 === 0 ? 'chart-appear' : 'chart-appear-alt';
  }

  get revealAnimationName(): string {
    return this.chartAnimationKey % 2 === 0 ? 'chart-wipe' : 'chart-wipe-alt';
  }

  ngOnInit(): void {
    void this.loadMatchedOffers();
    void this.ensureActiveLeaderboardLoaded();
    void this.loadSubscriptionUsage();
    this.replayChartAnimation();
  }

  ngOnDestroy(): void {
    this.cancelChartAnimationFrame();
    this.modalCloseTimers.forEach(timer => clearTimeout(timer));
    this.modalCloseTimers.clear();
  }

  offersByTier(tier: Offer['tier']): Offer[] {
    return this.tierListItems
      .filter(item => item.tier === tier)
      .map(item => this.offers.find(offer => offer.id === item.offerId))
      .filter((offer): offer is Offer => Boolean(offer));
  }

  get availableTierOffers(): Offer[] {
    const tierListOfferIds = new Set(this.tierListItems.map(item => item.offerId));
    const search = this.tierOfferSearch.trim().toLowerCase();

    return this.offers.filter(offer => {
      const isAvailable = !tierListOfferIds.has(offer.id);
      if (!isAvailable) return false;
      if (!search) return true;

      return offer.title.toLowerCase().includes(search)
        || offer.university.toLowerCase().includes(search)
        || offer.country.toLowerCase().includes(search)
        || offer.status.toLowerCase().includes(search)
        || String(offer.fitScore).includes(search)
        || offer.deadline.includes(search);
    });
  }

  setRange(range: RangePreset): void {
    this.selectedRange = range;
    this.resetChartInteraction();
    this.replayChartAnimation();
  }

  setMetric(metric: ChartMetric): void {
    this.selectedMetric = metric;
    this.resetChartInteraction();
    this.replayChartAnimation();
  }

  setMainTab(tab: DashboardMainTab): void {
    this.activeMainTab = tab;
    if (tab === 'contributors') void this.ensureActiveLeaderboardLoaded();
  }

  openContributionsTab(): void {
    this.setMainTab('contributors');
  }

  openMyContributionsModal(): void {
    this.cancelModalClose('myContributions');
    this.showMyContributionsModal = true;
    void this.loadMyContributions(1);
  }

  setMyContributionsTab(tab: MyContributionsTab): void {
    if (this.activeMyContributionsTab === tab || this.isLoadingMyContributions) return;
    this.activeMyContributionsTab = tab;
    this.myContributions = [];
    this.myContributionsResponse = null;
    void this.loadMyContributions(1);
  }

  setLeaderboardBoard(board: LeaderboardBoard): void {
    this.selectedLeaderboardBoard = board;
    this.selectedScoreContributor = null;
    void this.ensureActiveLeaderboardLoaded();
  }

  openScoreDetails(contributor: TopContributor): void {
    this.cancelModalClose('scoreDetails');
    this.selectedScoreContributor = contributor;
  }

  setCustomFrom(value: string): void {
    this.customFrom = this.normalizeDateInput(value, this.statPoints[0].date);
    this.resetChartInteraction();
    this.replayChartAnimation();
  }

  setCustomTo(value: string): void {
    this.customTo = this.normalizeDateInput(value, this.today);
    this.resetChartInteraction();
    this.replayChartAnimation();
  }

  onDragStart(event: DragEvent, offer: Offer): void {
    event.dataTransfer?.setData('text/plain', offer.id);
    event.dataTransfer?.setData('source-tier', offer.tier);
  }

  allowDrop(event: DragEvent): void {
    event.preventDefault();
  }

  dropOnTier(event: DragEvent, tier: Offer['tier']): void {
    event.preventDefault();
    const offerId = event.dataTransfer?.getData('text/plain');
    this.moveOfferToTier(offerId, tier);
  }

  dropBeforeOffer(event: DragEvent, targetOffer: Offer): void {
    event.preventDefault();
    const offerId = event.dataTransfer?.getData('text/plain');
    if (!offerId || offerId === targetOffer.id) return;

    const targetTierItem = this.tierListItems.find(item => item.offerId === targetOffer.id);
    if (!targetTierItem) return;

    this.moveTierListOffer(offerId, targetTierItem.tier, targetOffer.id);
  }

  openCalendarModal(offer: Offer): void {
    if (this.isUnknownDeadline(offer.deadline)) return;
    this.cancelModalClose('calendar');
    this.selectedOffer = offer;
  }

  openOfferDetail(offer: Offer): void {
    this.cancelModalClose('offerDetail');
    this.selectedOfferDetail = offer;
    this.isOfferDetailScrolled = false;
    this.generatedCoverLetter = '';
    this.coverLetterRequestMessageKey = '';
    this.offerContributions = null;
    this.contributionMessage = '';
    this.contributionDrafts = this.emptyContributionDrafts();
    void this.refreshSelectedOfferDetail(offer);
    void this.loadOfferContributions(offer);
    void this.loadGeneratedCoverLetter(offer);
  }

  openOfferDetailFromModal(offer: Offer): void {
    this.cancelModalClose('addTierOffer');
    this.showAddTierOfferModal = false;
    this.openOfferDetail(offer);
  }

  async openOfferDetailFromContributionSummary(contribution: UserContributionSummary): Promise<void> {
    const offerId = contribution.offerId.trim();
    if (!offerId) return;
    this.closeScoreDetails(true);
    this.closeMyContributionsModal(true);
    const existingOffer = this.offers.find(offer => offer.id === offerId);
    if (existingOffer) {
      this.openOfferDetail(existingOffer);
      return;
    }
    try {
      this.openOfferDetail(await this.api.getOffer(offerId));
    } catch (error) {
      console.warn('Unable to open contributed offer detail.', error);
    }
  }

  closeOfferDetail(): void {
    this.closeContributionModal(true);
    this.startModalClose('offerDetail', () => {
      this.selectedOfferDetail = null;
      this.generatedCoverLetter = '';
      this.coverLetterDraft = '';
      this.coverLetterRequestMessageKey = '';
      this.offerContributions = null;
      this.contributionMessage = '';
      this.isOfferDetailScrolled = false;
    });
  }

  onOfferDetailBodyScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    this.isOfferDetailScrolled = Boolean(target && target.scrollTop > 8);
  }

  get canContributeOfferInfo(): boolean {
    return Boolean(this.subscriptionEntitlements?.offerContributionsEnabled)
      || this.paidPlanCanContribute(this.subscriptionEntitlements?.plan)
      || this.currentSessionPlanCanContribute();
  }

  private currentSessionPlanCanContribute(): boolean {
    return this.paidPlanCanContribute(this.session.currentUser()?.planTier);
  }

  private paidPlanCanContribute(plan: unknown): boolean {
    const normalizedPlan = String(plan ?? '').trim().toLowerCase();
    return ['base', 'candidate', 'pro', 'researchpro', 'research_pro', 'research pro'].includes(normalizedPlan);
  }

  get contributionFields(): ContributionField[] {
    const offer = this.selectedOfferDetail;
    return [
      { key: 'deadline', label: this.i18n.t('common.deadline'), missing: !offer || this.isUnknownDeadline(offer.deadline), placeholder: '2026-09-15' },
      { key: 'country', label: this.i18n.t('common.country'), missing: !offer || this.isUnknownCountry(offer.country) || this.isMultipleCountries(offer.country), placeholder: 'France' },
      { key: 'university', label: this.i18n.t('common.university'), missing: !offer || this.isUnknownOrganization(offer.university), placeholder: 'University of Example' },
      { key: 'publishedAt', label: this.i18n.t('dashboard.publishedAt'), missing: !offer || this.isMissingOptionalText(offer.publishedAt), placeholder: '2026-08-01' },
      { key: 'department', label: this.i18n.t('dashboard.offerDetails.department'), missing: !offer || this.isMissingOptionalText(offer.department), placeholder: 'Computer Science' },
      { key: 'researchAreas', label: this.i18n.t('dashboard.offerDetails.researchAreas'), missing: !offer || this.isMissingOptionalList(offer.researchAreas), placeholder: 'AI, computational biology' },
      { key: 'methods', label: this.i18n.t('dashboard.offerDetails.methods'), missing: !offer || this.isMissingOptionalList(offer.methods), placeholder: 'Machine learning, statistics' },
      { key: 'requirements', label: this.i18n.t('dashboard.offerDetails.requirements'), missing: !offer || this.isMissingOptionalList(offer.requirements), placeholder: 'CV, transcript' },
      { key: 'benefits', label: this.i18n.t('dashboard.offerDetails.benefits'), missing: !offer || this.isMissingOptionalList(offer.benefits), placeholder: 'Salary, travel funding' },
      { key: 'description', label: this.i18n.t('dashboard.offerDetails.description'), missing: !offer || this.isMissingOptionalText(offer.description), placeholder: 'Short offer description' },
      { key: 'sourceUrl', label: this.i18n.t('dashboard.offerDetails.sourceLink'), missing: !offer || this.isMissingOptionalText(offer.sourceUrl), placeholder: 'https://example.edu/offer' },
      { key: 'applyUrl', label: this.i18n.t('dashboard.offerDetails.applyLink'), missing: !offer || this.isMissingOptionalText(offer.applyUrl), placeholder: 'https://example.edu/apply' }
    ];
  }

  get hasMissingContributionFields(): boolean {
    return this.contributionFields.some(field => field.missing);
  }

  get topContributionProposals(): OfferContributionProposal[] {
    if (!this.selectedContributionField) return [];
    return this.topContributionsForField(this.selectedContributionField.key);
  }

  isUnknownOrganization(value: string | undefined): boolean {
    const normalized = String(value ?? '').trim().toLowerCase();
    return !normalized || normalized === 'unknown organization' || normalized === 'research institution' || normalized === 'unknown';
  }

  openContributionModal(field: ContributionField): void {
    this.cancelModalClose('contribution');
    this.selectedContributionField = field;
    this.contributionMessage = '';
  }

  closeContributionModal(force = false): void {
    if (this.isSubmittingContribution) return;
    if (!force && !this.selectedContributionField) return;
    this.startModalClose('contribution', () => {
      this.selectedContributionField = null;
      this.contributionMessage = '';
    });
  }

  isOfferLinkContributionField(field: ContributionField | null): boolean {
    return field?.key === 'sourceUrl' || field?.key === 'applyUrl';
  }

  returnToOfferModalFromContribution(): void {
    this.closeContributionModal();
  }

  contributionCurrentValue(field: ContributionField): string {
    if (!this.selectedOfferDetail) return this.i18n.t('common.unknown');
    const offer = this.selectedOfferDetail;
    switch (field.key) {
      case 'deadline': return this.formatOfferDeadline(offer.deadline);
      case 'country': return this.countryDisplayName(offer.country);
      case 'university': return offer.university || this.i18n.t('common.unknown');
      case 'publishedAt': return this.formatOptionalOfferDate(offer.publishedAt);
      case 'department': return offer.department || this.i18n.t('common.unknown');
      case 'researchAreas': return this.contributionListValue(offer.researchAreas);
      case 'methods': return this.contributionListValue(offer.methods);
      case 'requirements': return this.contributionListValue(offer.requirements);
      case 'benefits': return this.contributionListValue(offer.benefits);
      case 'description': return offer.description || this.i18n.t('common.unknown');
      case 'sourceUrl': return offer.sourceUrl || this.i18n.t('common.unknown');
      case 'applyUrl': return offer.applyUrl || this.i18n.t('common.unknown');
    }
  }

  topContributionsForField(field: ContributionFieldKey): OfferContributionProposal[] {
    return [...(this.offerContributions?.fields?.[field] ?? [])]
      .sort((a, b) => ((b.score ?? b.voteCount) - (a.score ?? a.voteCount)) || (b.voteCount - a.voteCount) || ((a.dislikeCount ?? 0) - (b.dislikeCount ?? 0)) || a.value.localeCompare(b.value))
      .slice(0, 3);
  }

  async reactToContribution(proposal: OfferContributionProposal, reaction: ContributionReaction): Promise<void> {
    await this.submitContribution(proposal.field as ContributionFieldKey, proposal.value, reaction);
  }

  async submitSelectedContribution(): Promise<void> {
    if (!this.selectedContributionField) return;
    await this.submitContribution(this.selectedContributionField.key);
  }

  async submitContribution(field: ContributionFieldKey, value = this.contributionDrafts[field], reaction: ContributionReaction = 'like'): Promise<void> {
    if (!this.selectedOfferDetail || this.isSubmittingContribution || !this.canContributeOfferInfo) return;
    const normalizedValue = value.trim();
    if (!normalizedValue) return;
    this.isSubmittingContribution = true;
    this.contributionMessage = '';
    try {
      this.offerContributions = await this.api.contributeOfferInfo(this.selectedOfferDetail.id, field, normalizedValue, reaction);
      if (value === this.contributionDrafts[field]) this.contributionDrafts = { ...this.contributionDrafts, [field]: '' };
      this.contributionMessage = this.i18n.t(reaction === 'dislike' ? 'dashboard.contributionModal.feedbackRecorded' : 'dashboard.contributionModal.contributionRecorded');
      await this.refreshSelectedOfferDetail(this.selectedOfferDetail);
    } catch (error) {
      console.warn('Unable to submit offer contribution.', error);
      this.contributionMessage = this.i18n.t('dashboard.contributionModal.recordFailed');
    } finally {
      this.isSubmittingContribution = false;
    }
  }

  contributionProposalStats(proposal: OfferContributionProposal): string {
    return this.i18n.t('dashboard.contributionModal.proposalStats', {
      score: proposal.score ?? proposal.voteCount,
      likes: proposal.voteCount,
      dislikes: proposal.dislikeCount ?? 0
    });
  }

  private contributionListValue(value: string[] | undefined): string {
    const meaningfulValues = (value ?? []).filter(item => !this.isMissingOptionalText(item));
    return meaningfulValues.length ? meaningfulValues.join(', ') : this.i18n.t('common.unknown');
  }

  private emptyContributionDrafts(): Record<ContributionFieldKey, string> {
    return {
      deadline: '',
      country: '',
      university: '',
      publishedAt: '',
      department: '',
      researchAreas: '',
      methods: '',
      requirements: '',
      benefits: '',
      description: '',
      sourceUrl: '',
      applyUrl: ''
    };
  }

  private isMissingOptionalText(value: string | undefined): boolean {
    const normalized = String(value ?? '').trim().toLowerCase();
    return !normalized || normalized === 'unknown' || normalized === 'unspecified' || normalized === 'n/a' || normalized === 'not provided';
  }

  private isMissingOptionalList(value: string[] | undefined): boolean {
    return !value?.length || value.every(item => this.isMissingOptionalText(item));
  }

  async generateCoverLetter(offer: Offer): Promise<void> {
    if (this.isGeneratingCoverLetter || !this.canRequestCoverLetter) return;

    this.isGeneratingCoverLetter = true;
    this.generatedCoverLetter = '';
    this.coverLetterRequestMessageKey = '';
    try {
      const response = await this.api.requestCoverLetter(offer.id);
      if (response.quota) this.coverLetterUsage = this.mapCoverLetterQuota(response.quota);
      this.coverLetterRequestMessageKey = 'dashboard.coverLetterQueued';
      void this.loadGeneratedCoverLetter(offer);
    } catch (error) {
      console.warn('Unable to queue cover-letter generation.', error);
      this.coverLetterRequestMessageKey = 'dashboard.coverLetterFailed';
    } finally {
      this.isGeneratingCoverLetter = false;
    }
  }

  private async refreshSelectedOfferDetail(offer: Offer): Promise<void> {
    this.isRefreshingOfferDetail = true;
    try {
      const refreshed = await this.api.getOffer(offer.id);
      if (this.selectedOfferDetail?.id !== offer.id) return;
      this.selectedOfferDetail = refreshed;
      this.offers = this.offers.map(item => item.id === refreshed.id ? refreshed : item);
    } catch (error) {
      console.warn('Unable to refresh offer detail.', error);
    } finally {
      if (this.selectedOfferDetail?.id === offer.id) this.isRefreshingOfferDetail = false;
    }
  }

  private async loadGeneratedCoverLetter(offer: Offer): Promise<void> {
    this.isLoadingGeneratedCoverLetter = true;
    try {
      const response = await this.api.getCoverLetter(offer.id);
      if (this.selectedOfferDetail?.id !== offer.id) return;
      this.generatedCoverLetter = response.status === 'READY' ? response.coverLetter : '';
      this.coverLetterDraft = this.generatedCoverLetter;
    } catch (error) {
      console.warn('Generated cover letter could not be loaded.', error);
      if (this.selectedOfferDetail?.id === offer.id) this.generatedCoverLetter = '';
      if (this.selectedOfferDetail?.id === offer.id) this.coverLetterDraft = '';
    } finally {
      if (this.selectedOfferDetail?.id === offer.id) this.isLoadingGeneratedCoverLetter = false;
    }
  }

  private async loadOfferContributions(offer: Offer): Promise<void> {
    this.isLoadingOfferContributions = true;
    try {
      const response = await this.api.listOfferContributions(offer.id);
      if (this.selectedOfferDetail?.id !== offer.id) return;
      this.offerContributions = response;
    } catch (error) {
      console.warn('Offer contributions could not be loaded.', error);
      if (this.selectedOfferDetail?.id === offer.id) this.offerContributions = null;
    } finally {
      if (this.selectedOfferDetail?.id === offer.id) this.isLoadingOfferContributions = false;
    }
  }

  async saveCoverLetterDraft(): Promise<void> {
    if (!this.selectedOfferDetail || !this.generatedCoverLetter || this.isSavingCoverLetter) return;
    const draft = this.coverLetterDraft.trim();
    if (!draft) return;
    this.isSavingCoverLetter = true;
    this.coverLetterRequestMessageKey = '';
    try {
      const response = await this.api.updateCoverLetter(this.selectedOfferDetail.id, draft);
      if (this.selectedOfferDetail?.id !== response.offerId) return;
      this.generatedCoverLetter = response.coverLetter;
      this.coverLetterDraft = response.coverLetter;
      this.coverLetterRequestMessageKey = 'dashboard.coverLetterSaved';
    } catch (error) {
      console.warn('Unable to save generated cover letter.', error);
      this.coverLetterRequestMessageKey = 'dashboard.coverLetterSaveFailed';
    } finally {
      this.isSavingCoverLetter = false;
    }
  }

  async exportCoverLetterTxt(): Promise<void> {
    if (!this.selectedOfferDetail || !this.coverLetterDraft.trim() || this.isExportingCoverLetterTxt) return;
    this.isExportingCoverLetterTxt = true;
    try {
      await this.shortActionDelay();
      const blob = new Blob([this.coverLetterDraft.trim() + '\n'], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.slugify(this.selectedOfferDetail.title)}-cover-letter.txt`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      this.isExportingCoverLetterTxt = false;
    }
  }

  async exportCoverLetterPdf(): Promise<void> {
    if (!this.selectedOfferDetail || !this.coverLetterDraft.trim() || this.isExportingCoverLetterPdf) return;
    this.isExportingCoverLetterPdf = true;
    try {
      await this.shortActionDelay();
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      const filename = `${this.selectedOfferDetail.title}.pdf`;
      printWindow.document.write(this.coverLetterPrintHtml(filename, this.coverLetterDraft.trim()));
      printWindow.document.close();
      this.printCoverLetterWindow(printWindow);
    } finally {
      this.isExportingCoverLetterPdf = false;
    }
  }

  get canRequestCoverLetter(): boolean {
    return !this.coverLetterUsage || this.coverLetterUsage.remaining > 0;
  }

  get coverLetterQuotaLabel(): string {
    if (this.isLoadingCoverLetterQuota) return this.i18n.t('dashboard.coverLetterQuotaLoading');
    if (!this.coverLetterUsage) return this.i18n.t('dashboard.coverLetterQuotaUnavailable');
    return this.i18n.t('dashboard.coverLetterQuota', {
      remaining: this.coverLetterUsage.remaining,
      limit: this.coverLetterUsage.limit,
      reset: this.coverLetterUsage.resetAt ? this.i18n.formatDate(this.coverLetterUsage.resetAt.slice(0, 10)) : this.i18n.t('common.unknown')
    });
  }

  async updateSelectedOfferStatus(status: Offer['status']): Promise<void> {
    if (!this.selectedOfferDetail) return;
    const previousOffer = this.selectedOfferDetail;
    const optimisticOffer = { ...previousOffer, status };
    this.replaceOffer(optimisticOffer);
    this.selectedOfferDetail = optimisticOffer;
    try {
      const updatedOffer = await this.api.updateOfferStatus(previousOffer.id, status);
      this.replaceOffer(updatedOffer);
      this.selectedOfferDetail = updatedOffer;
    } catch (error) {
      console.warn('Unable to update offer status; reverting local change.', error);
      this.replaceOffer(previousOffer);
      this.selectedOfferDetail = previousOffer;
    }
  }

  goToOffersPage(page: number): void {
    const targetPage = Math.max(1, Math.min(this.totalOffersPages, Math.trunc(Number(page) || 1)));
    this.currentOffersPage = targetPage;
    this.customOffersPage = targetPage;
    if (!this.hasActiveOfferFilters) void this.loadMatchedOffers(targetPage);
  }

  goToPreviousOffersPage(): void {
    this.goToOffersPage(this.currentOffersPage - 1);
  }

  goToNextOffersPage(): void {
    this.goToOffersPage(this.currentOffersPage + 1);
  }

  setCustomOffersPage(value: number | string): void {
    this.customOffersPage = Math.trunc(Number(value) || 1);
  }

  goToCustomOffersPage(): void {
    this.goToOffersPage(this.customOffersPage);
  }

  setOffersPageSize(value: number | string): void {
    const pageSize = Number(value);
    this.offersPageSize = this.pageSizeOptions.includes(pageSize) ? pageSize : this.pageSizeOptions[0];
    this.goToOffersPage(1);
  }

  setOfferSort(key: OfferSortKey): void {
    if (this.offerSortKey !== key) {
      this.offerSortKey = key;
      this.offerSortDirection = 'desc';
    } else if (this.offerSortDirection === 'desc') {
      this.offerSortDirection = 'asc';
    } else {
      this.offerSortKey = null;
      this.offerSortDirection = null;
    }
    this.goToOffersPage(1);
  }

  sortIcon(key: OfferSortKey): string {
    if (this.offerSortKey !== key || !this.offerSortDirection) return 'bi-arrow-down-up';
    return this.offerSortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }

  formatOptionalOfferDate(value: string | undefined): string {
    if (!value || value === 'unknown') return this.i18n.t('common.unknown');

    return this.i18n.formatDate(value.slice(0, 10));
  }

  isUnknownDeadline(value: string | undefined): boolean {
    const normalized = String(value ?? '').trim().toLowerCase();
    return !normalized
      || normalized === 'unknown'
      || normalized === 'open deadline'
      || normalized === 'rolling'
      || normalized.startsWith(Dashboard.OPEN_DEADLINE_SENTINEL);
  }

  formatOfferDeadline(value: string | undefined): string {
    if (this.isUnknownDeadline(value)) return this.i18n.t('common.unknown');
    const deadline = value ?? '';
    return this.i18n.formatDate(deadline.slice(0, 10));
  }

  openSearchModal(): void {
    this.cancelModalClose('search');
    this.draftOfferFilters = { ...this.offerFilters };
    this.showSearchModal = true;
  }

  closeScoreDetails(force = false): void {
    if (force) {
      this.cancelModalClose('scoreDetails');
      this.selectedScoreContributor = null;
      return;
    }
    this.startModalClose('scoreDetails', () => {
      this.selectedScoreContributor = null;
    });
  }

  closeSearchModal(): void {
    this.startModalClose('search', () => {
      this.showSearchModal = false;
    });
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.selectedContributionField) {
      this.closeContributionModal();
      return;
    }
    if (this.selectedOfferDetail) {
      this.closeOfferDetail();
      return;
    }
    if (this.selectedOffer) {
      this.closeCalendarModal();
      return;
    }
    if (this.showAddTierOfferModal) {
      this.closeAddTierOfferModal();
      return;
    }
    if (this.showMyContributionsModal) {
      this.closeMyContributionsModal();
      return;
    }
    if (this.selectedScoreContributor) {
      this.closeScoreDetails();
      return;
    }
    if (this.showSearchModal) this.closeSearchModal();
  }

  closeMyContributionsModal(force = false): void {
    if (force) {
      this.cancelModalClose('myContributions');
      this.showMyContributionsModal = false;
      return;
    }
    this.startModalClose('myContributions', () => {
      this.showMyContributionsModal = false;
    });
  }

  setDraftOfferFilter(key: keyof OfferFilters, value: string): void {
    this.draftOfferFilters = { ...this.draftOfferFilters, [key]: value };
  }

  async applyOfferFilters(): Promise<void> {
    if (this.isApplyingOfferFilters || this.isClearingOfferFilters) return;

    this.isApplyingOfferFilters = true;
    await this.shortActionDelay();
    this.offerFilters = { ...this.draftOfferFilters };
    this.currentOffersPage = 1;
    this.customOffersPage = 1;
    this.closeSearchModal();
    this.isApplyingOfferFilters = false;
  }

  async clearOfferFilters(): Promise<void> {
    if (this.isApplyingOfferFilters || this.isClearingOfferFilters) return;

    this.isClearingOfferFilters = true;
    await this.shortActionDelay();
    this.draftOfferFilters = this.emptyOfferFilters();
    this.offerFilters = this.emptyOfferFilters();
    this.currentOffersPage = 1;
    this.customOffersPage = 1;
    await this.loadMatchedOffers(1);
    this.isClearingOfferFilters = false;
  }

  openAddTierOfferModal(tier: Offer['tier'] = 'S'): void {
    if (this.isAddingTierOffer) return;

    this.cancelModalClose('addTierOffer');
    this.selectedTierForNewOffer = tier;
    this.tierOfferSearch = '';
    this.showAddTierOfferModal = true;
    this.closeTierContextMenu();
  }

  closeAddTierOfferModal(): void {
    if (this.isAddingTierOffer) return;

    this.startModalClose('addTierOffer', () => {
      this.showAddTierOfferModal = false;
    });
  }

  contributionSummaryStats(contribution: UserContributionSummary): string {
    return this.i18n.t('dashboard.contributionModal.proposalStats', {
      score: contribution.score,
      likes: contribution.voteCount,
      dislikes: contribution.dislikeCount
    });
  }

  contributionFieldLabel(field: string): string {
    return this.contributionFields.find(item => item.key === field)?.label ?? field;
  }

  contributionOfferLabel(contribution: UserContributionSummary): string {
    const offer = this.offers.find(item => item.id === contribution.offerId);
    return offer?.title || contribution.offerId || this.i18n.t('common.unknown');
  }

  myContributionDate(contribution: UserContributionSummary): string {
    return contribution.createdAt ? this.i18n.formatDate(contribution.createdAt.slice(0, 10)) : this.i18n.t('common.unknown');
  }

  myContributionsEmptyTitleKey(): string {
    return this.activeMyContributionsTab === 'liked'
      ? 'dashboard.myContributionsModal.emptyLikedTitle'
      : 'dashboard.myContributionsModal.emptySentTitle';
  }

  myContributionsEmptyDescriptionKey(): string {
    return this.activeMyContributionsTab === 'liked'
      ? 'dashboard.myContributionsModal.emptyLikedDescription'
      : 'dashboard.myContributionsModal.emptySentDescription';
  }

  async goToMyContributionsPage(page: number): Promise<void> {
    const targetPage = Math.max(1, Math.min(this.myContributionsTotalPages, Math.trunc(Number(page) || 1)));
    await this.loadMyContributions(targetPage);
  }

  async goToPreviousMyContributionsPage(): Promise<void> {
    await this.goToMyContributionsPage(this.currentMyContributionsPage - 1);
  }

  async goToNextMyContributionsPage(): Promise<void> {
    await this.goToMyContributionsPage(this.currentMyContributionsPage + 1);
  }

  async loadMyContributions(page: number): Promise<void> {
    if (this.isLoadingMyContributions) return;
    this.isLoadingMyContributions = true;
    this.myContributionsLoadFailed = false;
    const tab = this.activeMyContributionsTab;
    try {
      const response = await this.api.listMyContributions(page, this.myContributionsPageSize, tab);
      if (!this.showMyContributionsModal || this.activeMyContributionsTab !== tab) return;
      this.myContributionsResponse = response;
      this.myContributions = response.items;
      this.currentMyContributionsPage = response.page;
    } catch (error) {
      console.warn('My contributions could not be loaded.', error);
      if (this.showMyContributionsModal) this.myContributionsLoadFailed = true;
    } finally {
      this.isLoadingMyContributions = false;
    }
  }

  async addOfferToTierList(offerId: Offer['id'], tier: Offer['tier'] = this.selectedTierForNewOffer): Promise<void> {
    if (this.isAddingTierOffer || this.tierListItems.some(item => item.offerId === offerId)) return;

    this.isAddingTierOffer = true;
    const previousTierListItems = this.tierListItems;
    const previousOffer = this.offers.find(offer => offer.id === offerId);
    this.tierListItems = [...this.tierListItems, { offerId, tier }];
    if (previousOffer) this.replaceOffer({ ...previousOffer, saved: true });
    try {
      await this.shortActionDelay();
      const updatedOffer = await this.api.saveOffer(offerId);
      this.replaceOffer({ ...updatedOffer, tier });
      this.isAddingTierOffer = false;
      this.closeAddTierOfferModal();
    } catch (error) {
      console.warn('Unable to save offer; reverting tier-list change.', error);
      this.tierListItems = previousTierListItems;
      if (previousOffer) this.replaceOffer(previousOffer);
    } finally {
      this.isAddingTierOffer = false;
    }
  }

  openTierContextMenu(event: MouseEvent, offer: Offer): void {
    event.preventDefault();
    this.tierContextMenu = { offerId: offer.id, x: event.clientX, y: event.clientY };
  }

  closeTierContextMenu(): void {
    this.tierContextMenu = null;
  }

  async removeOfferFromTierList(offerId: Offer['id'] | undefined = this.tierContextMenu?.offerId): Promise<void> {
    if (!offerId) return;

    const previousTierListItems = this.tierListItems;
    const previousOffer = this.offers.find(offer => offer.id === offerId);
    this.tierListItems = this.tierListItems.filter(item => item.offerId !== offerId);
    if (previousOffer) this.replaceOffer({ ...previousOffer, saved: false });
    this.closeTierContextMenu();
    try {
      const updatedOffer = await this.api.unsaveOffer(offerId);
      this.replaceOffer(updatedOffer);
    } catch (error) {
      console.warn('Unable to unsave offer; reverting tier-list change.', error);
      this.tierListItems = previousTierListItems;
      if (previousOffer) this.replaceOffer(previousOffer);
    }
  }

  async downloadIcs(): Promise<void> {
    if (!this.selectedOffer || this.isUnknownDeadline(this.selectedOffer.deadline) || this.isDownloadingIcs) return;

    this.isDownloadingIcs = true;
    try {
      await this.shortActionDelay();
      const blob = new Blob([this.buildIcs(this.selectedOffer)], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.slugify(this.selectedOffer.title)}-deadline.ics`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      this.isDownloadingIcs = false;
    }
  }

  closeCalendarModal(): void {
    this.startModalClose('calendar', () => {
      this.selectedOffer = null;
    });
  }

  isModalClosing(modal: DashboardModalKey): boolean {
    return this.closingModals.has(modal);
  }

  private startModalClose(modal: DashboardModalKey, finalize: () => void): void {
    if (this.closingModals.has(modal)) return;

    this.closingModals.add(modal);
    const timer = setTimeout(() => {
      finalize();
      this.closingModals.delete(modal);
      this.modalCloseTimers.delete(modal);
    }, this.modalAnimationDurationMs);
    this.modalCloseTimers.set(modal, timer);
  }

  private cancelModalClose(modal: DashboardModalKey): void {
    const timer = this.modalCloseTimers.get(modal);
    if (timer) clearTimeout(timer);
    this.modalCloseTimers.delete(modal);
    this.closingModals.delete(modal);
  }

  graphBarHeight(point: StatPoint): string {
    return `${Math.max(.75, point.newOffers * .75)}rem`;
  }

  fitPointY(point: StatPoint): number {
    return 120 - point.averageFit;
  }

  get averageFitPath(): string {
    return this.buildSmoothPath(this.animatedMetricPlotPoints(point => point.averageFit));
  }

  get newOffersPath(): string {
    const maxOffers = this.maxVisibleOffers;
    return this.buildSmoothPath(this.animatedMetricPlotPoints(point => (point.newOffers / maxOffers) * 100));
  }

  newOffersPointY(point: StatPoint): number {
    const maxOffers = this.maxVisibleOffers;
    return this.linePointY((point.newOffers / maxOffers) * 100);
  }

  selectedMetricPointY(point: StatPoint): number {
    return this.selectedMetric === 'averageFit' ? this.linePointY(point.averageFit) : this.newOffersPointY(point);
  }

  selectedMetricTooltipValue(point: StatPoint): string {
    return this.selectedMetric === 'averageFit' ? `${point.averageFit}%` : `${point.newOffers}`;
  }

  pointX(index: number): number {
    const leftPadding = 24;
    const rightPadding = 24;
    const plotWidth = 500 - leftPadding - rightPadding;
    const pointCount = this.visibleStatPoints.length;
    if (pointCount === 1) return 250;
    return leftPadding + (plotWidth / (pointCount - 1)) * index;
  }

  linePointY(value: number): number {
    const topPadding = 18;
    const bottomPadding = 138;
    const normalizedValue = Math.max(0, Math.min(100, value));
    return bottomPadding - (normalizedValue / 100) * (bottomPadding - topPadding);
  }

  showPointInfo(point: StatPoint, index: number): void {
    const x = this.pointX(index);
    const tooltipWidth = 116;
    const tooltipHeight = 58;
    const tooltipGap = 12;
    const tooltipX = Math.max(tooltipWidth / 2, Math.min(500 - tooltipWidth / 2, x));
    const highestMetricY = Math.min(this.linePointY(point.averageFit), this.newOffersPointY(point));
    const tooltipY = Math.max(tooltipHeight / 2, highestMetricY - tooltipHeight / 2 - tooltipGap);

    this.cursorGuideX = x;
    this.activeStatPoint = { point, index, x, tooltipX, tooltipY };
  }

  hidePointInfo(): void {
    this.activeStatPoint = null;
  }

  updateCursorGuide(event: PointerEvent): void {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const relativeX = Math.max(0, Math.min(500, ((event.clientX - rect.left) / rect.width) * 500));
    const closestIndex = this.closestStatPointIndex(relativeX);
    this.showPointInfo(this.visibleStatPoints[closestIndex], closestIndex);
  }

  hideCursorGuide(): void {
    this.cursorGuideX = null;
    this.hidePointInfo();
  }

  async openChartFullscreen(): Promise<void> {
    const chartElement = document.querySelector<HTMLElement>('.dashboard-chart-card');
    if (!chartElement) return;

    try {
      if (!document.fullscreenElement && chartElement.requestFullscreen) {
        await chartElement.requestFullscreen();
      }
      const orientation = screen.orientation as ScreenOrientation & { lock?: (orientation: 'landscape') => Promise<void> };
      if (orientation.lock) await orientation.lock('landscape').catch(() => undefined);
      document.addEventListener('fullscreenchange', () => {
        if (!document.fullscreenElement) {
          const unlockableOrientation = screen.orientation as ScreenOrientation & { unlock?: () => void };
          unlockableOrientation.unlock?.();
        }
      }, { once: true });
    } catch {
      // Fullscreen and orientation lock are best-effort; some mobile browsers block one or both APIs.
    }
  }

  isActiveStatPoint(index: number): boolean {
    return this.activeStatPoint?.index === index;
  }

  private moveOfferToTier(offerId: string | undefined, tier: Offer['tier']): void {
    if (!offerId) return;
    this.moveTierListOffer(offerId, tier);
  }

  private moveTierListOffer(offerId: string, tier: Offer['tier'], beforeOfferId?: string): void {
    const existingItem = this.tierListItems.find(item => item.offerId === offerId);
    if (!existingItem) return;

    const movedItem: TierListItem = { ...existingItem, tier };
    const withoutMovedItem = this.tierListItems.filter(item => item.offerId !== offerId);
    const targetIndex = beforeOfferId ? withoutMovedItem.findIndex(item => item.offerId === beforeOfferId) : -1;

    if (targetIndex >= 0) {
      withoutMovedItem.splice(targetIndex, 0, movedItem);
      this.tierListItems = withoutMovedItem;
      return;
    }

    this.tierListItems = [...withoutMovedItem, movedItem];
  }

  private replaceOffer(updatedOffer: Offer): void {
    this.offers = this.offers.map(offer => offer.id === updatedOffer.id ? updatedOffer : offer);
  }

  private rangeOfPages(start: number, end: number): number[] {
    if (end < start) return [];
    return Array.from({ length: end - start + 1 }, (_item, index) => start + index);
  }

  private paginationPageItem(page: number): PaginationItem {
    return { key: `page-${page}`, label: String(page), page };
  }

  private compareOffers(a: Offer, b: Offer): number {
    if (!this.offerSortKey || !this.offerSortDirection) return 0;

    const directionMultiplier = this.offerSortDirection === 'asc' ? 1 : -1;
    let comparison = 0;

    if (this.offerSortKey === 'status') {
      comparison = this.statusOrder.indexOf(a.status) - this.statusOrder.indexOf(b.status);
    } else if (this.offerSortKey === 'title') {
      comparison = a.title.localeCompare(b.title);
    } else if (this.offerSortKey === 'university') {
      comparison = a.university.localeCompare(b.university);
    } else if (this.offerSortKey === 'country') {
      comparison = this.countryDisplayName(a.country).localeCompare(this.countryDisplayName(b.country));
    } else if (this.offerSortKey === 'fitScore') {
      comparison = a.fitScore - b.fitScore;
    } else if (this.offerSortKey === 'publishedAt') {
      comparison = (a.publishedAt ?? '').localeCompare(b.publishedAt ?? '');
    } else {
      comparison = a.deadline.localeCompare(b.deadline);
    }

    if (comparison === 0) comparison = a.title.localeCompare(b.title);
    return comparison * directionMultiplier;
  }

  private emptyOfferFilters(): OfferFilters {
    return {
      status: '',
      title: '',
      university: '',
      country: '',
      fitScore: '',
      deadline: ''
    };
  }

  private offerMatchesFilters(offer: Offer, filters: OfferFilters): boolean {
    const normalizedFilters = Object.fromEntries(
      Object.entries(filters).map(([key, value]) => [key, value.trim().toLowerCase()])
    ) as OfferFilters;

    return (!normalizedFilters.status || offer.status.toLowerCase().includes(normalizedFilters.status))
      && (!normalizedFilters.title || offer.title.toLowerCase().includes(normalizedFilters.title))
      && (!normalizedFilters.university || offer.university.toLowerCase().includes(normalizedFilters.university))
      && (!normalizedFilters.country || offer.country.toLowerCase().includes(normalizedFilters.country))
      && (!normalizedFilters.fitScore || String(offer.fitScore).includes(normalizedFilters.fitScore.replace('%', '')))
      && (!normalizedFilters.deadline || offer.deadline.includes(normalizedFilters.deadline));
  }

  private countryCode(country: string): string {
    if (this.isUnknownCountry(country) || this.isMultipleCountries(country)) return '';

    const normalizedCountry = country.trim().toLowerCase();
    const directIsoCode = normalizedCountry.match(/^[a-z]{2}$/) ? normalizedCountry.toUpperCase() : '';
    if (directIsoCode) return directIsoCode;

    return this.countryCodeByDisplayName[normalizeCountryName(country)] ?? '';
  }

  private countryFlagItem(code: IsoCountryCode, displayName = isoCountryDisplayName(code, this.i18n.dateLocale())): CountryFlagItem {
    return {
      code,
      name: displayName,
      flagUrl: countryFlagImageUrl(code)
    };
  }

  private closestStatPointIndex(x: number): number {
    return this.visibleStatPoints.reduce((closestIndex, _point, index) => {
      const currentDistance = Math.abs(this.pointX(index) - x);
      const closestDistance = Math.abs(this.pointX(closestIndex) - x);
      return currentDistance < closestDistance ? index : closestIndex;
    }, 0);
  }

  private resetChartInteraction(): void {
    this.cursorGuideX = null;
    this.activeStatPoint = null;
  }

  private replayChartAnimation(): void {
    this.chartAnimationKey += 1;
    this.animatedPointCount = 0;
    this.cancelChartAnimationFrame();
    this.chartAnimationStartedAt = 0;
    this.chartAnimationFrame = requestAnimationFrame(timestamp => this.plotChartFrame(timestamp));
  }

  private plotChartFrame(timestamp: number): void {
    if (!this.chartAnimationStartedAt) this.chartAnimationStartedAt = timestamp;

    const pointCount = this.plotPointCount;
    if (pointCount <= 1) {
      this.animatedPointCount = pointCount;
      this.chartAnimationFrame = null;
      return;
    }

    const elapsed = Math.min(timestamp - this.chartAnimationStartedAt, this.chartPlotDuration);
    const progress = elapsed / this.chartPlotDuration;
    const easedProgress = progress < .5 ? 2 * progress * progress : 1 - ((-2 * progress + 2) ** 2) / 2;
    this.animatedPointCount = Math.min(pointCount, Math.max(2, Math.floor(easedProgress * (pointCount - 1)) + 1));

    if (elapsed < this.chartPlotDuration) {
      this.chartAnimationFrame = requestAnimationFrame(nextTimestamp => this.plotChartFrame(nextTimestamp));
      return;
    }

    this.animatedPointCount = pointCount;
    this.chartAnimationFrame = null;
  }

  private get plotPointCount(): number {
    return this.visibleStatPoints.length <= 1 ? this.visibleStatPoints.length : this.virtualPlotPointCount;
  }

  private animatedMetricPlotPoints(valueGetter: (point: StatPoint) => number): { x: number; y: number }[] {
    const realPoints = this.visibleStatPoints.map((point, index) => ({
      x: this.pointX(index),
      y: this.linePointY(valueGetter(point))
    }));
    const realPointCount = realPoints.length;
    const virtualPointCount = this.plotPointCount;
    const animatedCount = Math.max(0, Math.min(virtualPointCount, this.animatedPointCount));

    if (realPointCount === 0 || animatedCount === 0) return [];
    if (realPointCount === 1 || animatedCount === 1) {
      return [realPoints[0]];
    }

    return Array.from({ length: animatedCount }, (_item, virtualIndex) => {
      const progress = virtualIndex / (virtualPointCount - 1);
      const scaledRealIndex = progress * (realPointCount - 1);
      const leftIndex = Math.min(realPointCount - 2, Math.floor(scaledRealIndex));
      const rightIndex = Math.min(realPointCount - 1, leftIndex + 1);
      const t = scaledRealIndex - leftIndex;
      const p0 = realPoints[Math.max(0, leftIndex - 1)];
      const p1 = realPoints[leftIndex];
      const p2 = realPoints[rightIndex];
      const p3 = realPoints[Math.min(realPointCount - 1, leftIndex + 2)];
      const control1 = {
        x: p1.x + (p2.x - p0.x) / 6,
        y: p1.y + (p2.y - p0.y) / 6
      };
      const control2 = {
        x: p2.x - (p3.x - p1.x) / 6,
        y: p2.y - (p3.y - p1.y) / 6
      };

      return this.cubicBezierPoint(p1, control1, control2, p2, t);
    });
  }

  private cubicBezierPoint(
    start: { x: number; y: number },
    control1: { x: number; y: number },
    control2: { x: number; y: number },
    end: { x: number; y: number },
    t: number
  ): { x: number; y: number } {
    const inverseT = 1 - t;
    const startWeight = inverseT ** 3;
    const control1Weight = 3 * inverseT ** 2 * t;
    const control2Weight = 3 * inverseT * t ** 2;
    const endWeight = t ** 3;

    return {
      x: start.x * startWeight + control1.x * control1Weight + control2.x * control2Weight + end.x * endWeight,
      y: start.y * startWeight + control1.y * control1Weight + control2.y * control2Weight + end.y * endWeight
    };
  }

  private cancelChartAnimationFrame(): void {
    if (this.chartAnimationFrame !== null) {
      cancelAnimationFrame(this.chartAnimationFrame);
      this.chartAnimationFrame = null;
    }
  }

  private shortActionDelay(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 250));
  }

  private buildSmoothPath(points: { x: number; y: number }[]): string {
    if (points.length === 0) return '';
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

    const commands = [`M ${points[0].x} ${points[0].y}`];

    for (let index = 0; index < points.length - 1; index += 1) {
      const p0 = points[Math.max(0, index - 1)];
      const p1 = points[index];
      const p2 = points[index + 1];
      const p3 = points[Math.min(points.length - 1, index + 2)];
      const control1X = p1.x + (p2.x - p0.x) / 6;
      const control1Y = p1.y + (p2.y - p0.y) / 6;
      const control2X = p2.x - (p3.x - p1.x) / 6;
      const control2Y = p2.y - (p3.y - p1.y) / 6;

      commands.push(`C ${control1X} ${control1Y}, ${control2X} ${control2Y}, ${p2.x} ${p2.y}`);
    }

    return commands.join(' ');
  }

  private normalizeDateInput(value: string, fallback: string): string {
    if (!value) return fallback;
    return value > this.today ? this.today : value;
  }

  private shiftDate(isoDate: string, days: number): string {
    const date = new Date(`${isoDate}T00:00:00`);
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
  }

  private buildYearlyStatPoints(endDate: string): StatPoint[] {
    return Array.from({ length: 365 }, (_item, index) => {
      const daysFromStart = index;
      const date = this.shiftDate(endDate, daysFromStart - 364);
      const seasonalWave = Math.sin((daysFromStart / 364) * Math.PI * 2);
      const weeklyWave = Math.sin((daysFromStart / 7) * Math.PI * 2);
      const newOffers = Math.max(1, Math.round(4 + seasonalWave * 2 + weeklyWave * 1.5 + (index % 5 === 0 ? 2 : 0)));
      const averageFit = Math.max(52, Math.min(97, Math.round(74 + seasonalWave * 10 + (daysFromStart / 364) * 8 + weeklyWave * 3)));

      return {
        date,
        day: this.formatChartDay(date),
        newOffers,
        averageFit
      };
    });
  }

  private async loadMatchedOffers(page = this.currentOffersPage): Promise<void> {
    this.isLoadingMatchedOffers = true;
    this.matchedOffersLoadFailed = false;
    try {
      const response = await this.api.listMatchedOffers(page, this.offersPageSize, this.offerSortKey, this.offerSortDirection);
      const matchedOffers = response.items;
      this.totalMatchedOffers = response.totalItems;
      this.totalMatchedOfferPages = response.totalPages;
      this.matchedOffersLoadFailed = false;
      this.offers = matchedOffers;
      this.tierListItems = matchedOffers
        .filter(offer => offer.saved)
        .map(offer => ({ offerId: offer.id, tier: offer.tier }));
      this.currentOffersPage = Math.max(1, Math.min(response.page, this.totalOffersPages));
      this.customOffersPage = this.currentOffersPage;
    } catch (error) {
      this.matchedOffersLoadFailed = true;
      this.offers = [];
      this.tierListItems = [];
      console.warn('Matched offers could not be loaded.', error);
    } finally {
      this.isLoadingMatchedOffers = false;
    }
  }

  private async loadSubscriptionUsage(): Promise<void> {
    this.isLoadingCoverLetterQuota = true;
    try {
      const entitlements = await this.api.getSubscriptionEntitlements();
      this.subscriptionEntitlements = entitlements;
    } catch (error) {
      this.subscriptionEntitlements = null;
      console.warn('Subscription entitlements could not be loaded.', error);
    }

    try {
      const usage = await this.api.getUsage('COVER_LETTER');
      this.coverLetterUsage = usage;
    } catch (error) {
      this.coverLetterUsage = null;
      console.warn('Cover-letter usage could not be loaded.', error);
    } finally {
      this.isLoadingCoverLetterQuota = false;
    }
  }

  async loadTopContributors(): Promise<void> {
    delete this.leaderboardCache[this.selectedLeaderboardBoard];
    await this.ensureActiveLeaderboardLoaded(true);
  }

  async ensureActiveLeaderboardLoaded(force = false): Promise<void> {
    const cached = this.leaderboardCache[this.selectedLeaderboardBoard];
    if (!force && cached && Date.now() - cached.loadedAt < this.leaderboardCacheTtlMs) {
      this.applyLeaderboardResponse(cached.response);
      return;
    }
    this.isLoadingTopContributors = true;
    this.topContributorsLoadFailed = false;
    try {
      const response = await this.api.listTopContributors(10, this.selectedLeaderboardBoard);
      this.leaderboardCache[this.selectedLeaderboardBoard] = { loadedAt: Date.now(), response };
      this.applyLeaderboardResponse(response);
    } catch (error) {
      this.topContributors = [];
      this.topContributorsResponse = null;
      this.topContributorsLoadFailed = true;
      console.warn('Top contributors could not be loaded.', error);
    } finally {
      this.isLoadingTopContributors = false;
    }
  }

  private applyLeaderboardResponse(response: TopContributorsResponse): void {
    this.topContributorsResponse = response;
    this.topContributors = response.items;
    this.topContributorsLoadFailed = false;
    this.resetContributorAvatarLoadingState(response);
  }

  contributorInitials(contributor: TopContributor): string {
    const name = (contributor.username || contributor.displayName || '?').replace(/^@/, '').trim();
    return (name.slice(0, 2) || '?').toUpperCase();
  }

  rankMedal(rank: number): string {
    return rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
  }

  onContributorAvatarError(event: Event): void {
    const image = event.target as HTMLImageElement | null;
    if (!image) return;
    this.markContributorAvatarLoaded(image.dataset['avatarKey']);
    image.remove();
  }

  onContributorAvatarLoad(avatarKey: string): void {
    this.markContributorAvatarLoaded(avatarKey);
  }

  contributorAvatarKey(contributor: TopContributor): string {
    return `${this.selectedLeaderboardBoard}:${contributor.rank}:${contributor.username || contributor.displayName}:${contributor.avatar || ''}`;
  }

  safeExternalUrl(value: string | undefined): string {
    return sanitizeExternalUrl(value);
  }

  safeImageUrl(value: string | undefined): string {
    return safeSameOriginOrHttpImageUrl(value);
  }

  isContributorAvatarLoading(contributor: TopContributor): boolean {
    return Boolean(this.safeImageUrl(contributor.avatar)) && this.loadingContributorAvatarKeys.has(this.contributorAvatarKey(contributor));
  }

  private resetContributorAvatarLoadingState(response: TopContributorsResponse): void {
    this.loadingContributorAvatarKeys = new Set(
      [...response.items, response.currentUser]
        .filter((contributor): contributor is TopContributor => Boolean(this.safeImageUrl(contributor?.avatar)))
        .map(contributor => this.contributorAvatarKey(contributor))
    );
  }

  private markContributorAvatarLoaded(avatarKey: string | undefined): void {
    if (!avatarKey) return;
    this.loadingContributorAvatarKeys.delete(avatarKey);
  }

  selectedLeaderboardLabel(): string {
    return this.i18n.t(this.leaderboardBoards.find(board => board.value === this.selectedLeaderboardBoard)?.labelKey ?? 'dashboard.leaderboards.overall');
  }

  scoreMetricCards(contributor: TopContributor): ScoreMetricCard[] {
    return [
      { key: 'suggestions', labelKey: 'dashboard.scoreModal.suggestions', value: contributor.proposalCount, icon: 'bi-lightbulb' },
      { key: 'likes', labelKey: 'dashboard.scoreModal.likesReceived', value: contributor.receivedLikeCount, icon: 'bi-hand-thumbs-up' },
      { key: 'dislikes', labelKey: 'dashboard.scoreModal.dislikesReceived', value: contributor.receivedDislikeCount, icon: 'bi-hand-thumbs-down' },
      { key: 'votes', labelKey: 'dashboard.scoreModal.votesCast', value: contributor.voteCount, icon: 'bi-check2-circle' },
      { key: 'reviewed', labelKey: 'dashboard.scoreModal.reviewed', value: contributor.reviewCount, icon: 'bi-search' },
      { key: 'score', labelKey: 'dashboard.scoreModal.totalScore', value: contributor.score, icon: 'bi-stars' }
    ];
  }

  scoreCompositionSegments(contributor: TopContributor): ScoreCompositionSegment[] {
    const suggestions = Math.max(0, contributor.proposalCount);
    const likes = Math.max(0, contributor.receivedLikeCount);
    const dislikes = Math.max(0, contributor.receivedDislikeCount);
    const votes = Math.max(0, contributor.voteCount);
    const target = this.scoreCompositionTarget(contributor);
    const segments: ScoreCompositionSegment[] = [
      { key: 'suggestions', labelKey: 'dashboard.scoreModal.suggestions', count: suggestions, weight: 2, points: suggestions * 2, value: suggestions * 2, width: ((suggestions * 2) / target) * 100, color: '#2563EB' },
      { key: 'likes', labelKey: 'dashboard.scoreModal.likesReceived', count: likes, weight: 1, points: likes, value: likes, width: (likes / target) * 100, color: '#10B981', kind: 'positive' },
      { key: 'votes', labelKey: 'dashboard.scoreModal.votesCast', count: votes, weight: 1, points: votes, value: votes, width: (votes / target) * 100, color: '#8B5CF6', kind: 'neutral' },
      { key: 'dislikes', labelKey: 'dashboard.scoreModal.dislikesReceived', count: dislikes, weight: -1, points: -dislikes, value: dislikes, width: Math.max(dislikes ? ((dislikes / target) * 100) : 0, 0), color: '#EF4444', kind: 'negative' }
    ];
    return segments.map(segment => ({ ...segment, width: segment.points === 0 ? 0 : Math.max(5, Math.abs(segment.width)) }));
  }

  scoreCompositionTarget(contributor: TopContributor): number {
    const positivePoints = Math.max(0, contributor.proposalCount) * 2 + Math.max(0, contributor.receivedLikeCount) + Math.max(0, contributor.voteCount);
    return Math.max(10, positivePoints, Math.abs(contributor.score));
  }

  scoreComponentFormula(segment: ScoreCompositionSegment): string {
    const points = segment.points > 0 ? `+${segment.points}` : String(segment.points);
    return `${segment.count} × ${segment.weight} = ${points} pts`;
  }

  sentimentSegments(contributor: TopContributor): ChartSegment[] {
    const likes = Math.max(0, contributor.receivedLikeCount);
    const dislikes = Math.max(0, contributor.receivedDislikeCount);
    const total = Math.max(1, likes + dislikes);
    return [
      { key: 'likes', labelKey: 'dashboard.scoreModal.likesReceived', value: likes, width: (likes / total) * 100, kind: 'positive' },
      { key: 'dislikes', labelKey: 'dashboard.scoreModal.dislikesReceived', value: dislikes, width: (dislikes / total) * 100, kind: 'negative' }
    ];
  }

  sentimentPositivePercent(contributor: TopContributor): number {
    const likes = Math.max(0, contributor.receivedLikeCount);
    const dislikes = Math.max(0, contributor.receivedDislikeCount);
    const total = likes + dislikes;
    return total ? Math.round((likes / total) * 100) : 0;
  }

  chartPercentLabel(width: number): string {
    return `${Math.round(width)}%`;
  }

  activitySegments(contributor: TopContributor): ChartSegment[] {
    const sent = Math.max(0, contributor.proposalCount);
    const reviewed = Math.max(0, contributor.reviewCount);
    const total = Math.max(1, sent + reviewed);
    return [
      { key: 'sent', labelKey: 'dashboard.scoreModal.sent', value: sent, width: (sent / total) * 100, kind: 'neutral' },
      { key: 'reviewed', labelKey: 'dashboard.scoreModal.reviewed', value: reviewed, width: (reviewed / total) * 100, kind: 'positive' }
    ];
  }

  badgeLabel(badge: ContributorBadge): string {
    const labelKey = badge.labelKey || this.badgeLabelKey(badge.key);
    return this.i18n.t(labelKey);
  }

  badgeAlt(badge: ContributorBadge): string {
    return this.i18n.t(badge.altKey || this.badgeAltKey(badge.key));
  }

  badgeImageUrl(badge: ContributorBadge): string {
    if (badge.imageUrl) return this.safeImageUrl(badge.imageUrl) || this.defaultBadgeImageUrl;
    if (badge.key === 'complete_profile') return 'assets/badges/complete-profile.svg';
    if (badge.key === 'helpful_reviewer' || badge.key === 'top_reviewer_2026') return 'assets/badges/top-reviewer-2026.svg';
    return this.defaultBadgeImageUrl;
  }

  private badgeLabelKey(key: string): string {
    return `dashboard.badges.${key || 'default'}.label`;
  }

  private badgeAltKey(key: string): string {
    return `dashboard.badges.${key || 'default'}.alt`;
  }

  activitySplit(contributor: TopContributor): { sentWidth: number; reviewedWidth: number } {
    const total = Math.max(1, contributor.proposalCount + contributor.reviewCount);
    return {
      sentWidth: (contributor.proposalCount / total) * 100,
      reviewedWidth: (contributor.reviewCount / total) * 100
    };
  }

  private mapCoverLetterQuota(quota: Record<string, unknown>): UsageStatus {
    return {
      feature: String(quota['feature'] ?? 'COVER_LETTER'),
      period: String(quota['period'] ?? ''),
      limit: Number(quota['limit'] ?? 0),
      used: Number(quota['used'] ?? 0),
      reserved: Number(quota['reserved'] ?? 0),
      remaining: Number(quota['remaining'] ?? 0),
      resetAt: String(quota['resetAt'] ?? quota['reset_at'] ?? '')
    };
  }

  private formatChartDay(isoDate: string): string {
    return new Intl.DateTimeFormat(this.i18n.dateLocale(), { month: 'short', day: '2-digit' }).format(new Date(`${isoDate}T00:00:00`));
  }

  private buildIcs(offer: Offer): string {
    if (this.isUnknownDeadline(offer.deadline)) return '';
    const date = offer.deadline.replaceAll('-', '');
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//PhD Compass//Deadline Export//EN',
      'BEGIN:VEVENT',
      `UID:${offer.id}@phdcompass`,
      `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
      `DTSTART;VALUE=DATE:${date}`,
      `SUMMARY:${this.i18n.t('dashboard.icsSummary', { title: offer.title })}`,
      `DESCRIPTION:${this.i18n.t('dashboard.icsDescription', { university: offer.university, country: offer.country, status: this.offerStatusLabel(offer.status), fit: offer.fitScore })}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
  }

  private coverLetterPrintHtml(title: string, coverLetter: string): string {
    return `<!doctype html>
<html lang="${this.i18n.currentLanguage()}">
<head>
  <meta charset="utf-8">
  <title>${this.escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #172033; line-height: 1.55; margin: 2rem; }
    pre { white-space: pre-wrap; font: inherit; margin: 0; }
    @media print { body { margin: 1.5cm; } }
  </style>
</head>
<body>
  <pre>${this.escapeHtml(coverLetter)}</pre>
</body>
</html>`;
  }

  private printCoverLetterWindow(printWindow: Window): void {
    const print = (): void => {
      printWindow.focus();
      printWindow.print();
    };

    if (printWindow.document.readyState === 'complete') {
      window.setTimeout(print, 0);
      return;
    }

    printWindow.addEventListener('load', print, { once: true });
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private slugify(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }
}